import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useSelector } from 'react-redux'

import map from 'lodash/map'
import camelCase from 'lodash/camelCase'

import * as sessionUserSelector from 'selectors/sessionUser'

import { useLocationQueryParams, useSetSearchParamsWithQueryString } from 'utils/hooks'
import { userTimeZoneFormat } from 'utils/time'
import { complianceLabel } from 'utils/satCompliance'

import {
  useFetchPartnerSatClientsQuery,
} from 'api/partnerSatClient'

import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Header from 'components/Shared/Header/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import FilterPanel from 'components/Shared/Skeleton/FilterPanel/Index'
import FilterPanelTitle from 'components/Shared/Skeleton/FilterPanel/FilterPanelTitle'
import MixedFiltersField from 'components/Shared/MixedFiltersField/Index'
import RowsCountAndPagination from 'components/Shared/RowsCountAndPagination/Index'
import Tooltip from 'components/Shared/Tooltip/Index'

import SatClientsTable from './SatClientsTable'
import SatClientSidebar from './SatClientSidebar'
import FiltersSidebar from './FiltersSidebar'

const MULTIPLE_FILTER_NAMES = ['capabilities', 'compliance_statuses']

const DATE_FILTER_NAMES = [
  'expires_at_start_date', 'expires_at_end_date',
  'last_used_at_start_date', 'last_used_at_end_date',
]

const SatClientsPage = () => {
  const navigate = useNavigate()

  const setQueryParam = useSetSearchParamsWithQueryString()
  const [locationQueryParams, location] = useLocationQueryParams()
  const activePage = parseInt(locationQueryParams.page) || 1
  const activeFilters = locationQueryParams.filters || {}
  const userTimeZone = useSelector(sessionUserSelector.timezone)

  const { data, isFetching } = useFetchPartnerSatClientsQuery({
    page: activePage,
    filters: activeFilters,
  })
  const satClients = data?.satClients || []
  const totalPages = data?.meta?.totalPages || 1
  const satClientsCount = data?.meta?.count || 0
  const capabilitiesList = data?.meta.capabilities || []

  const [selectedSatClient, setSelectedSatClient] = useState(null)
  const [isFiltersSidebarVisible, setIsFiltersSidebarVisible] = useState(false)

  const { t } = useTranslation()

  useEffect(() => { setSelectedSatClient(null) }, [isFetching])

  const onRowClick = (satClient) => {
    setSelectedSatClient(selectedSatClient?.id === satClient.id ? null : satClient)
  }

  const onSidebarClose = () => {
    setSelectedSatClient(null)
  }

  const getBreadcrumbs = () => {
    const breadcrumbsList = [{ text: t('common.breadcrumbSatClients') }]
    return breadcrumbsList
  }

  const onFilterChange = (filterData) => {
    const newActiveFilters = {
      ...activeFilters,
      ...{ [filterData.name]: filterData.value },
    }

    if (!filterData.value) {
      delete newActiveFilters[filterData.name]
    }

    setQueryParam({ filters: newActiveFilters })
  }

  const toggleFiltersSidebar = () => {
    setIsFiltersSidebarVisible(!isFiltersSidebarVisible)
  }

  const showFiltersSidebar = () => {
    setIsFiltersSidebarVisible(true)
  }

  const hideFiltersSidebar = () => {
    setIsFiltersSidebarVisible(false)
  }

  const buildCapabilityFilterOptions = () => {
    return capabilitiesList.map(capability => {
      return { value: capability, label: capability }
    })
  }

  const buildSelectedFilterList = () => {
    const result = []

    map(activeFilters, (value, name) => {
      if (!MULTIPLE_FILTER_NAMES.includes(name)) {
        result.push({
          value: DATE_FILTER_NAMES.includes(name) ? userTimeZoneFormat(userTimeZone, value) : value,
          title: t(`satClientsPage.filters.${camelCase(name)}`),
          clearTooltip: t('satClientsPage.clearFilterTooltip', { filterName: name }),
          onClear: () => onFilterChange({ name: name, value: '' }),
        })
      }
    })

    return result
  }

  const buildSelectedMultipleFilterList = () => {
    const result = []

    if (activeFilters.capabilities?.length) {
      result.push({
        title: t('satClientsPage.filters.capabilities'),
        clearTooltip: t('satClientsPage.clearFilterTooltip', { filterName: 'Capabilities' }),
        onClear: () => onFilterChange({ name: 'capabilities', value: [] }),
        items: activeFilters.capabilities.map(capability => buildCapabilityFilterOptions().find(item => item.value === capability)),
      })
    }

    if (activeFilters.compliance_statuses?.length) {
      result.push({
        title: t('satClientsPage.filters.compliance'),
        clearTooltip: t('satClientsPage.clearFilterTooltip', { filterName: 'Compliance' }),
        onClear: () => onFilterChange({ name: 'compliance_statuses', value: [] }),
        items: activeFilters.compliance_statuses.map(status => ({ value: complianceLabel(t, status) })),
      })
    }

    return result
  }

  return (
    <MainWrap pageTitle={t('satClientsPage.pageHeader')}>
      <FiltersSidebar
        capabilityList={buildCapabilityFilterOptions()}
        activeFilters={activeFilters}
        isVisible={isFiltersSidebarVisible}
        onClose={hideFiltersSidebar}
        onFilterChange={onFilterChange}
      />

      <SatClientSidebar
        satClient={selectedSatClient}
        onClose={onSidebarClose}
      />

      <Content loading={isFetching}>
        <Header
          title={t('satClientsPage.pageHeader')}
          breadcrumbsList={getBreadcrumbs()}
        />

        <FilterPanel dataTest='sat-clients-page-filter-panel'>
          <FilterPanelTitle
            title={t('satClientsPage.filterPanelTitle')}
            tooltipText={t('satClientsPage.filterPanelTitleTooltip')}
            onClick={toggleFiltersSidebar}
            active={isFiltersSidebarVisible}
          />

          <MixedFiltersField
            filterList={buildSelectedFilterList()}
            multipleFilterList={buildSelectedMultipleFilterList()}
            maxDisplayItems={3}
            onClear={() => navigate(location.pathname)}
            onClearTooltip={t('satClientsPage.clearAllFiltersTooltip')}
            tooltipText={t('satClientsPage.filterPanelFiltersTooltip')}
            tooltipId='tooltip-filter-panel'
            onContractorClick={showFiltersSidebar}
          />

          <Tooltip id='tooltip-filter-panel' />
        </FilterPanel>

        <SatClientsTable
          satClients={satClients}
          selectedSatClientId={selectedSatClient?.id}
          onRowClick={onRowClick}
        />

        <RowsCountAndPagination
          countText={t('satClientsPage.clientsCount', { count: satClientsCount })}
          activePage={activePage}
          totalPages={totalPages}
          isFetching={isFetching}
          dataTest='sat-clients-count'
        />
      </Content>
    </MainWrap>
  )
}

export default SatClientsPage
